<template>
  <div class="user-management">
    <div class="page-header">
      <h2>用户管理</h2>
      <div class="header-actions">
        <el-button @click="loadUsers" :loading="loading">刷新</el-button>
      </div>
    </div>

    <!-- 筛选器 -->
    <div class="filters">
      <el-form :model="filters" inline>
        <el-form-item label="用户状态">
          <el-select v-model="filters.status" placeholder="全部状态" clearable>
            <el-option label="活跃" value="active" />
            <el-option label="非活跃" value="inactive" />
            <el-option label="已封禁" value="banned" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="filters.device_type" placeholder="全部设备" clearable>
            <el-option label="Windows" value="Windows" />
            <el-option label="Mac" value="Mac" />
            <el-option label="Linux" value="Linux" />
            <el-option label="Android" value="Android" />
            <el-option label="iOS" value="iOS" />
          </el-select>
        </el-form-item>
        <el-form-item label="搜索">
          <el-input 
            v-model="filters.search" 
            placeholder="设备名称/设备ID/IP地址/位置"
            clearable
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadUsers">搜索</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 用户列表 -->
    <el-table 
      :data="users" 
      :loading="loading"
      stripe
      style="width: 100%"
    >
      <el-table-column prop="id" label="ID" width="60" />
      
      <el-table-column label="卡密信息" width="200">
        <template #default="{ row }">
          <div>
            <div class="card-key">{{ row.card_key }}</div>
            <el-tag :type="getCardTypeColor(row.card_type)" size="small">
              {{ getCardTypeName(row.card_type) }}
            </el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="设备信息" width="250">
        <template #default="{ row }">
          <div>
            <div class="device-name">{{ row.device_name || '未知设备' }}</div>
            <div class="device-info">
              <el-tag size="small">{{ row.device_type }}</el-tag>
              <span class="device-id">{{ row.device_id }}</span>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="ip_address" label="IP地址" width="120" />
      <el-table-column prop="location" label="位置" width="100" />

      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <div>
            <el-tag :type="getStatusColor(row.status)">
              {{ getStatusName(row.status) }}
            </el-tag>
            <div v-if="row.is_online" class="online-indicator">
              <el-tag type="success" size="small">在线</el-tag>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="total_login_count" label="登录次数" width="80" />
      <el-table-column prop="last_active_at" label="最后活跃" width="150">
        <template #default="{ row }">
          {{ formatDate(row.last_active_at) }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="viewUserDetail(row)">详情</el-button>
          <el-button 
            v-if="row.is_online" 
            size="small" 
            type="warning" 
            @click="kickUser(row)"
          >
            踢出
          </el-button>
          <el-button 
            size="small" 
            :type="row.status === 'banned' ? 'success' : 'danger'"
            @click="toggleUserStatus(row)"
          >
            {{ row.status === 'banned' ? '解封' : '封禁' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.limit"
        :page-sizes="[10, 20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="loadUsers"
        @current-change="loadUsers"
      />
    </div>

    <!-- 用户详情对话框 -->
    <el-dialog
      v-model="showDetailDialog"
      title="用户详情"
      width="800px"
    >
      <div v-if="selectedUser" class="user-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="用户ID">{{ selectedUser.id }}</el-descriptions-item>
          <el-descriptions-item label="设备名称">{{ selectedUser.device_name }}</el-descriptions-item>
          <el-descriptions-item label="设备类型">{{ selectedUser.device_type }}</el-descriptions-item>
          <el-descriptions-item label="设备ID">{{ selectedUser.device_id }}</el-descriptions-item>
          <el-descriptions-item label="IP地址">{{ selectedUser.ip_address }}</el-descriptions-item>
          <el-descriptions-item label="位置">{{ selectedUser.location }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusColor(selectedUser.status)">
              {{ getStatusName(selectedUser.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="总登录次数">{{ selectedUser.total_login_count }}</el-descriptions-item>
          <el-descriptions-item label="首次登录">{{ formatDate(selectedUser.first_login_at) }}</el-descriptions-item>
          <el-descriptions-item label="最后活跃">{{ formatDate(selectedUser.last_active_at) }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDate(selectedUser.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="卡密信息">
            <div>
              <div>{{ selectedUser.card_key }}</div>
              <el-tag :type="getCardTypeColor(selectedUser.card_type)" size="small">
                {{ getCardTypeName(selectedUser.card_type) }}
              </el-tag>
            </div>
          </el-descriptions-item>
        </el-descriptions>

        <div v-if="selectedUser.status === 'banned'" class="ban-info">
          <h4>封禁信息</h4>
          <p><strong>封禁原因：</strong>{{ selectedUser.ban_reason }}</p>
          <p><strong>封禁时间：</strong>{{ formatDate(selectedUser.banned_at) }}</p>
        </div>
      </div>
    </el-dialog>

    <!-- 封禁用户对话框 -->
    <el-dialog
      v-model="showBanDialog"
      title="封禁用户"
      width="500px"
    >
      <el-form :model="banForm" label-width="80px">
        <el-form-item label="封禁原因" required>
          <el-input 
            v-model="banForm.ban_reason" 
            type="textarea" 
            :rows="3"
            placeholder="请输入封禁原因"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showBanDialog = false">取消</el-button>
          <el-button type="danger" @click="confirmBanUser" :loading="banning">
            确定封禁
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { userAPI } from '@/services/api'

export default {
  name: 'UserManagement',
  setup() {
    
    const loading = ref(false)
    const banning = ref(false)
    const showDetailDialog = ref(false)
    const showBanDialog = ref(false)
    const users = ref([])
    const selectedUser = ref(null)
    const userToBan = ref(null)

    const filters = reactive({
      status: '',
      device_type: '',
      search: ''
    })

    const pagination = reactive({
      page: 1,
      limit: 20,
      total: 0
    })

    const banForm = reactive({
      ban_reason: ''
    })

    // 加载用户列表
    const loadUsers = async () => {
      try {
        loading.value = true
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...filters
        }
        
        // 移除空值
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const response = await userAPI.getUsers(params)
        if (response.success) {
          users.value = response.data.users
          pagination.total = response.data.pagination.total
        } else {
          ElMessage.error(response.message || '获取用户列表失败')
        }
      } catch (error) {
        ElMessage.error('获取用户列表失败: ' + error.message)
      } finally {
        loading.value = false
      }
    }

    // 重置筛选器
    const resetFilters = () => {
      Object.assign(filters, {
        status: '',
        device_type: '',
        search: ''
      })
      pagination.page = 1
      loadUsers()
    }

    // 查看用户详情
    const viewUserDetail = async (user) => {
      try {
        const response = await userAPI.getUserDetail(user.id)
        if (response.success) {
          selectedUser.value = response.data.user
          showDetailDialog.value = true
        } else {
          ElMessage.error(response.message || '获取用户详情失败')
        }
      } catch (error) {
        ElMessage.error('获取用户详情失败: ' + error.message)
      }
    }

    // 踢出用户
    const kickUser = async (user) => {
      try {
        await ElMessageBox.confirm(
          `确定要踢出用户 "${user.device_name}" 吗？`,
          '确认踢出',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await userAPI.kickUser(user.id)
        if (response.success) {
          ElMessage.success('用户已被踢出')
          loadUsers()
        } else {
          ElMessage.error(response.message || '踢出用户失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('踢出用户失败: ' + error.message)
        }
      }
    }

    // 切换用户状态
    const toggleUserStatus = (user) => {
      if (user.status === 'banned') {
        unbanUser(user)
      } else {
        banUser(user)
      }
    }

    // 封禁用户
    const banUser = (user) => {
      userToBan.value = user
      banForm.ban_reason = ''
      showBanDialog.value = true
    }

    // 确认封禁用户
    const confirmBanUser = async () => {
      if (!banForm.ban_reason.trim()) {
        ElMessage.error('请输入封禁原因')
        return
      }

      try {
        banning.value = true
        const response = await userAPI.updateUserStatus(userToBan.value.id, {
          status: 'banned',
          ban_reason: banForm.ban_reason
        })

        if (response.success) {
          ElMessage.success('用户已被封禁')
          showBanDialog.value = false
          loadUsers()
        } else {
          ElMessage.error(response.message || '封禁用户失败')
        }
      } catch (error) {
        ElMessage.error('封禁用户失败: ' + error.message)
      } finally {
        banning.value = false
      }
    }

    // 解封用户
    const unbanUser = async (user) => {
      try {
        await ElMessageBox.confirm(
          `确定要解封用户 "${user.device_name}" 吗？`,
          '确认解封',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await userAPI.updateUserStatus(user.id, {
          status: 'active'
        })

        if (response.success) {
          ElMessage.success('用户已解封')
          loadUsers()
        } else {
          ElMessage.error(response.message || '解封用户失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('解封用户失败: ' + error.message)
        }
      }
    }

    // 获取卡密类型名称
    const getCardTypeName = (type) => {
      const names = {
        'AI_HOST_BASIC': 'AI主机基础版',
        'AI_HOST_PRO': 'AI主机专业版',
        'AI_BOX_BASIC': 'AI盒子基础版',
        'AI_BOX_PRO': 'AI盒子专业版',
        'ALL_IN_ONE': '全功能版'
      }
      return names[type] || type
    }

    // 获取卡密类型颜色
    const getCardTypeColor = (type) => {
      const colors = {
        'AI_HOST_BASIC': '',
        'AI_HOST_PRO': 'success',
        'AI_BOX_BASIC': 'info',
        'AI_BOX_PRO': 'warning',
        'ALL_IN_ONE': 'danger'
      }
      return colors[type] || ''
    }

    // 获取状态名称
    const getStatusName = (status) => {
      const names = {
        'active': '活跃',
        'inactive': '非活跃',
        'banned': '已封禁'
      }
      return names[status] || status
    }

    // 获取状态颜色
    const getStatusColor = (status) => {
      const colors = {
        'active': 'success',
        'inactive': 'info',
        'banned': 'danger'
      }
      return colors[status] || ''
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    }

    onMounted(() => {
      loadUsers()
    })

    return {
      loading,
      banning,
      showDetailDialog,
      showBanDialog,
      users,
      selectedUser,
      userToBan,
      filters,
      pagination,
      banForm,
      loadUsers,
      resetFilters,
      viewUserDetail,
      kickUser,
      toggleUserStatus,
      banUser,
      confirmBanUser,
      unbanUser,
      getCardTypeName,
      getCardTypeColor,
      getStatusName,
      getStatusColor,
      formatDate
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.filters {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.card-key {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  margin-bottom: 4px;
}

.device-name {
  font-weight: bold;
  margin-bottom: 4px;
}

.device-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-id {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #666;
}

.online-indicator {
  margin-top: 4px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.user-detail {
  margin-bottom: 20px;
}

.ban-info {
  margin-top: 20px;
  padding: 15px;
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
}

.ban-info h4 {
  margin: 0 0 10px 0;
  color: #f56c6c;
}

.ban-info p {
  margin: 5px 0;
}
</style>
